import { useState, useMemo } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { trpc } from "@/lib/trpc";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  ComposedChart,
  Bar,
  Cell,
  Line,
  ReferenceLine,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
} from "recharts";
import {
  Download,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Activity,
  Maximize2,
  TrendingUp,
  TrendingDown,
  ShieldAlert,
  GitBranch,
  Gauge,
  DollarSign,
  Layers,
  Filter,
} from "lucide-react";
import { format } from "date-fns";
import { utils, writeFile } from "xlsx";
import {
  DatePeriodFilter,
  useDatePeriodFilter,
} from "@/components/shared/DatePeriodFilter";
import { KpiStatCard } from "@/components/shared/KpiStatCard";

export const PARETO_STAGE_FILTERS: { code: string; label: string }[] = [
  { code: "ALL", label: "Tất cả khâu" },
  { code: "IQC", label: "IQC Vật tư" },
  { code: "CUTTING", label: "1. Cắt giấy" },
  { code: "PRINTING", label: "2. In Offset" },
  { code: "LAMINATING", label: "3. Cán màng" },
  { code: "COATING", label: "4. Phủ UV" },
  { code: "DIE_CUTTING", label: "5. Bế hộp" },
  { code: "GLUING", label: "6. Dán hộp" },
  { code: "FQC", label: "FQC Thành phẩm" },
];

const FISHBONE_COLOR_MAP: Record<
  string,
  { border: string; bg: string; badge: string; text: string; accent: string }
> = {
  MAN: {
    border: "border-blue-200",
    bg: "bg-blue-50/50",
    badge: "bg-blue-600 text-white",
    text: "text-blue-900",
    accent: "#2563eb",
  },
  MACHINE: {
    border: "border-rose-200",
    bg: "bg-rose-50/50",
    badge: "bg-rose-600 text-white",
    text: "text-rose-900",
    accent: "#e11d48",
  },
  MATERIAL: {
    border: "border-amber-200",
    bg: "bg-amber-50/50",
    badge: "bg-amber-600 text-white",
    text: "text-amber-900",
    accent: "#d97706",
  },
  METHOD: {
    border: "border-purple-200",
    bg: "bg-purple-50/50",
    badge: "bg-purple-600 text-white",
    text: "text-purple-900",
    accent: "#9333ea",
  },
  MEASUREMENT: {
    border: "border-cyan-200",
    bg: "bg-cyan-50/50",
    badge: "bg-cyan-600 text-white",
    text: "text-cyan-900",
    accent: "#0891b2",
  },
  ENVIRONMENT: {
    border: "border-emerald-200",
    bg: "bg-emerald-50/50",
    badge: "bg-emerald-600 text-white",
    text: "text-emerald-900",
    accent: "#059669",
  },
};

export function QCReports() {
  const navigate = useNavigate();
  const {
    period,
    setPeriod,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,
    dateParams,
  } = useDatePeriodFilter("thisMonth");

  const [machineId, setMachineId] = useState<string>("all");
  const [selectedStage, setSelectedStage] = useState<string>("ALL");
  const [selected4M, setSelected4M] = useState<string>("ALL");
  const [showParetoZoom, setShowParetoZoom] = useState<boolean>(false);

  const { data, isLoading } = useQuery(
    trpc.reports.getQCData.queryOptions({
      startDate: dateParams.from,
      endDate: dateParams.to,
      period,
      processStage: selectedStage !== "ALL" ? selectedStage : undefined,
      machineId: machineId !== "all" ? machineId : undefined,
    }),
  );

  const { data: paretoData, isLoading: isParetoLoading } = useQuery(
    trpc.reports.getQcParetoByStage.queryOptions({
      startDate: dateParams.from,
      endDate: dateParams.to,
      period,
      stage: selectedStage as any,
      machineId: machineId !== "all" ? machineId : undefined,
    }),
  );

  const { data: machines } = useQuery(
    trpc.machines.list.queryOptions({ limit: 100 }),
  );

  // Tổng hợp KPI toàn chuỗi (IQC + IPQC + FQC + NCR)
  const stats = useMemo(() => {
    if (!data) return null;
    const { materialStats, processStats, finalStats, ncrStats } = data as any;
    const total =
      (materialStats?.total || 0) +
      (processStats?.total || 0) +
      (finalStats?.total || 0);
    const passed =
      (materialStats?.passed || 0) +
      (processStats?.passed || 0) +
      (finalStats?.passed || 0);
    const failed =
      (materialStats?.failed || 0) +
      (processStats?.failed || 0) +
      (finalStats?.failed || 0);
    const warning =
      (materialStats?.warning || 0) +
      (processStats?.warning || 0) +
      (finalStats?.warning || 0);
    const inspecting =
      (materialStats?.inspecting || 0) +
      (processStats?.inspecting || 0) +
      (finalStats?.inspecting || 0);
    const completed = passed + failed + warning;

    return {
      total,
      passed,
      failed,
      warning,
      inspecting,
      passRate: completed > 0 ? Math.round((passed / completed) * 1000) / 10 : 100,
      failRate: completed > 0 ? Math.round((failed / completed) * 1000) / 10 : 0,
      ncrTotal: ncrStats?.total || 0,
      ncrOpen: ncrStats?.open || 0,
      ncrClosed: ncrStats?.closed || 0,
      ncrOverdue: ncrStats?.overdueCount || 0,
      ncrResolutionPct: ncrStats?.resolutionRatePct ?? 100,
      copqTotal: ncrStats?.totalCostImpact || 0,
      scrapCost: ncrStats?.scrapCost || 0,
      reworkCost: ncrStats?.reworkCost || 0,
      reprintCost: ncrStats?.reprintCost || 0,
    };
  }, [data]);

  // Danh sách Pareto 80/20 (đồng bộ cả getQcParetoByStage và getQCData)
  const paretoItems: any[] = useMemo(() => {
    const rawItems =
      (paretoData as any)?.items || (data as any)?.paretoData || [];
    const filtered =
      selected4M === "ALL"
        ? rawItems
        : rawItems.filter((item: any) => item.rootCause4M === selected4M);

    // Nếu lọc theo 4M, tính lại đường lũy kế Pareto 0..100% trên tập con để đường Pareto luôn chuẩn xác
    if (selected4M !== "ALL" && filtered.length > 0) {
      const totalSub = filtered.reduce(
        (sum: number, it: any) => sum + Number(it.totalQty ?? it.defectQty ?? 0),
        0,
      );
      let run = 0;
      return filtered.slice(0, 15).map((it: any, idx: number) => {
        const qty = Number(it.totalQty ?? it.defectQty ?? 0);
        const prevCum = totalSub > 0 ? (run / totalSub) * 100 : 0;
        run += qty;
        const pct = totalSub > 0 ? Number(((qty / totalSub) * 100).toFixed(1)) : 0;
        const cumPct =
          totalSub > 0
            ? idx === filtered.length - 1
              ? 100
              : Number(((run / totalSub) * 100).toFixed(1))
            : 0;
        return {
          ...it,
          totalQty: qty,
          percent: pct,
          cumulativePercent: cumPct,
          isVitalFew: prevCum < 80 || idx === 0,
        };
      });
    }

    return rawItems.slice(0, 15).map((it: any) => ({
      ...it,
      totalQty: Number(it.totalQty ?? it.defectQty ?? 0),
      occurrences: Number(it.occurrences ?? it.occurrenceCount ?? 1),
      percent: Number(it.percent ?? it.sharePct ?? 0),
      cumulativePercent: Number(it.cumulativePercent ?? it.cumulativePct ?? 0),
      stage: it.stage || it.processStage || "PRINTING",
    }));
  }, [paretoData, data, selected4M]);

  const vitalFewCount = useMemo(
    () => paretoItems.filter((i) => i.isVitalFew).length,
    [paretoItems],
  );

  const totalParetoNg = useMemo(
    () => paretoItems.reduce((sum, i) => sum + Number(i.totalQty || 0), 0),
    [paretoItems],
  );

  const paretoChartData = useMemo(() => {
    if (paretoItems.length === 1) {
      return [
        {
          defectCode: "Gốc (0%)",
          defectName: "Điểm xuất phát",
          totalQty: 0,
          percent: 0,
          cumulativePercent: 0,
          isVitalFew: true,
        },
        ...paretoItems,
      ];
    }
    return paretoItems;
  }, [paretoItems]);

  // Ma trận 8 công đoạn (IQC -> 6 khâu Offset -> FQC)
  const stageSummary: any[] = useMemo(() => {
    return (
      (paretoData as any)?.stageSummary || (data as any)?.stageSummary || []
    );
  }, [paretoData, data]);

  const bottleneckStage = useMemo(() => {
    return stageSummary.find((s: any) => s.isBottleneck) || null;
  }, [stageSummary]);

  // Phân tích 4M/6M (Radar Chart + Ishikawa Fishbone)
  const analysis4M6M: any[] = useMemo(() => {
    return (
      (paretoData as any)?.analysis4M6M || (data as any)?.analysis4M6M || []
    );
  }, [paretoData, data]);

  const topFishboneBones = useMemo(
    () =>
      analysis4M6M.filter((m) =>
        ["MAN", "MACHINE", "MATERIAL"].includes(m.code),
      ),
    [analysis4M6M],
  );

  const bottomFishboneBones = useMemo(
    () =>
      analysis4M6M.filter((m) =>
        ["METHOD", "MEASUREMENT", "ENVIRONMENT"].includes(m.code),
      ),
    [analysis4M6M],
  );

  // Dữ liệu SPC Control Chart (CL, UCL, Forecast)
  const spcSummary = useMemo(() => {
    return (paretoData as any)?.spcSummary || (data as any)?.spcSummary || null;
  }, [paretoData, data]);

  const spcChartData = useMemo(() => {
    const trend: any[] =
      (paretoData as any)?.spcTrend || (data as any)?.spcTrend || [];
    if (trend.length === 0) return [];
    const forecastPoint = spcSummary
      ? [
          {
            dateKey: "FORECAST",
            label: "Dự báo",
            sampledQty: Math.round(
              trend.reduce((s, d) => s + (d.sampledQty || 0), 0) / trend.length,
            ),
            defectQty: 0,
            defectRatePct: spcSummary.forecastNextRatePct,
            centerLinePct: spcSummary.centerLinePct,
            uclPct: spcSummary.avgUclPct,
            isOutOfControl:
              spcSummary.forecastNextRatePct > spcSummary.avgUclPct,
            isForecast: true,
          },
        ]
      : [];
    return [...trend, ...forecastPoint];
  }, [paretoData, data, spcSummary]);

  // Tiến độ NCR 8D Pipeline
  const ncrPipeline = useMemo(() => {
    return (
      (paretoData as any)?.ncrPipeline || (data as any)?.ncrPipeline || null
    );
  }, [paretoData, data]);

  // Danh sách biên bản kiểm tra chi tiết (IQC + IPQC + FQC)
  const combinedRecords = useMemo(() => {
    if (!data) return [];
    const materials = ((data as any).qcMaterials || []).map((r: any) => ({
      ...r,
      typeLabel: "IQC Vật tư",
      stageCode: "IQC",
      stepLabel: "IQC - Vật tư",
      codeLabel: r.material?.name || r.lotNumber || "Lô vật tư",
      poLabel: r.poNumber || r.lotNumber || "-",
      okCount:
        (r.sampleSize || r.qtyInspected || 0) - (r.ngSampleQty || 0),
      ngCount:
        r.ngSampleQty ||
        r.qtyRejected ||
        (r.criticalDefects || 0) + (r.majorDefects || 0) + (r.minorDefects || 0),
    }));
    const processes = ((data as any).qcProcesses || []).map((r: any) => ({
      ...r,
      typeLabel: "IPQC Công đoạn",
      stageCode: r.processStage || "PRINTING",
      stepLabel: r.processStep || r.processStage || "In Offset",
      codeLabel: r.productionOrder?.productName || "-",
      poLabel:
        r.productionOrder?.orderId || r.productionOrder?.poNumber || "-",
      okCount: Math.max(0, (r.qtySample || 0) - (r.qtyDefect || 0)),
      ngCount:
        r.qtyDefect ||
        (r.criticalDefects || 0) + (r.majorDefects || 0) + (r.minorDefects || 0),
    }));
    const finals = ((data as any).qcFinals || []).map((r: any) => ({
      ...r,
      typeLabel: "FQC Thành phẩm",
      stageCode: "FQC",
      stepLabel: "FQC Xuất xưởng",
      codeLabel: r.productionOrder?.productName || "-",
      poLabel:
        r.productionOrder?.orderId || r.productionOrder?.poNumber || "-",
      okCount:
        r.qtyPassed ??
        Math.max(0, (r.sampleSize || 0) - (r.ngSampleQty || 0)),
      ngCount:
        r.ngSampleQty ||
        r.qtyFailed ||
        (r.criticalDefects || 0) + (r.majorDefects || 0) + (r.minorDefects || 0),
    }));

    const all = [...materials, ...processes, ...finals].sort(
      (a, b) =>
        new Date(b.checkTime).getTime() - new Date(a.checkTime).getTime(),
    );

    return all.filter((r) => {
      if (selectedStage !== "ALL" && r.stageCode !== selectedStage) {
        return false;
      }
      return true;
    });
  }, [data, selectedStage]);

  // Xuất báo cáo Excel QM 4 Sheets chuyên nghiệp
  const handleExportExcel = () => {
    if (!data && paretoItems.length === 0) return;

    const wb = utils.book_new();

    if (paretoItems.length > 0) {
      const paretoSheetRows = paretoItems.map((item: any, idx: number) => ({
        Hạng: idx + 1,
        "Mã Lỗi": item.defectCode,
        "Tên Lỗi": item.defectName,
        "Công Đoạn": item.stageShortLabel || item.stageLabel || item.stage,
        "Nhóm 4M/6M": item.rootCause4M || "MACHINE",
        "Mức Độ": item.severity,
        "Số Lượng NG": item.totalQty,
        "Số Lô Lặp Lại": item.occurrences,
        "Tỷ Trọng (%)": item.percent,
        "Lũy Kế Pareto (%)": item.cumulativePercent,
        "Phân Nhóm 80/20": item.isVitalFew
          ? "80% Trọng Yếu (Vital Few)"
          : "20% Thứ Yếu (Trivial Many)",
      }));
      utils.book_append_sheet(
        wb,
        utils.json_to_sheet(paretoSheetRows),
        "1_Pareto_80_20",
      );
    }

    if (analysis4M6M.length > 0) {
      const rows4M = analysis4M6M.map((m: any) => ({
        "Mã 6M": m.code,
        "Nhóm Nguyên Nhân": m.label,
        "Số Vụ Phát Sinh": m.incidentCount,
        "Tỷ Trọng Sự Cố (%)": m.incidentSharePct,
        "Tổng Lượng Lỗi NG": m.defectQty,
        "Tỷ Trọng Lỗi NG (%)": m.defectSharePct,
        "Chi Phí COPQ (VNĐ)": m.copqAmount,
        "Nguyên Nhân Gốc Rễ Hàng Đầu": (m.causes || [])
          .map((c: any) => `${c.title} (${c.qty})`)
          .join("; "),
      }));
      utils.book_append_sheet(
        wb,
        utils.json_to_sheet(rows4M),
        "2_Ishikawa_4M6M",
      );
    }

    if (stageSummary.length > 0) {
      const rowsStage = stageSummary.map((s: any) => ({
        "Mã Khâu": s.stageCode,
        "Công Đoạn": s.stageLabel,
        "Tổng Lô Kiểm": s.totalBatches,
        "Đang Kiểm (WIP)": s.inspectingCount,
        "FPY Đạt Ngay (%)": s.fpyPct,
        "Tổng Mẫu Kiểm": s.totalSampled,
        "Tổng Mẫu NG": s.totalNg,
        "Tỷ Lệ Lỗi Mẫu (%)": s.defectRatePct,
        "Lỗi Nổi Bật": s.topDefectName,
      }));
      utils.book_append_sheet(
        wb,
        utils.json_to_sheet(rowsStage),
        "3_Pipeline_8_Khau",
      );
    }

    const exportRows = combinedRecords.map((r: any, idx: number) => ({
      STT: idx + 1,
      "Phân Loại": r.typeLabel,
      "Thời Gian": r.checkTime
        ? format(new Date(r.checkTime), "dd/MM/yyyy HH:mm")
        : "-",
      "Mã Lệnh / Lô": r.poLabel,
      "Sản Phẩm / Vật Tư": r.codeLabel,
      "Công Đoạn": r.stepLabel,
      "Trạng Thái":
        r.status === "PASSED"
          ? "ĐẠT"
          : r.status === "FAILED"
            ? "LỖI"
            : r.status === "INSPECTING"
              ? "ĐANG KIỂM"
              : "CẢNH BÁO",
      "Mẫu OK": r.okCount,
      "Mẫu NG": r.ngCount,
      "Người Kiểm": r.inspector?.fullName || "-",
      "Ghi Chú": r.notes || "",
    }));
    utils.book_append_sheet(
      wb,
      utils.json_to_sheet(exportRows),
      "4_Nhat_Ky_KCS",
    );

    writeFile(
      wb,
      `QM_Dashboard_ISO9001_${format(new Date(), "yyyyMMdd_HHmmss")}.xlsx`,
    );
  };

  return (
    <div className="space-y-3 pb-6 p-2 md:p-3.5" data-testid="qm-command-center">
      {/* ═══ TẦNG 1A: THANH ĐIỀU KHIỂN TINH GỌN (Rule 12 Date Filter Outermost Left) ═══ */}
      <div className="flex flex-wrap items-center justify-between gap-2 p-2 bg-card border border-border rounded-xl shadow-2xs">
        <div className="erp-list-filter-row flex flex-wrap items-center gap-2">
          <div className="date-filter-pos0 flex items-center shrink-0">
            <DatePeriodFilter
              period={period}
              setPeriod={setPeriod}
              customFrom={customFrom}
              setCustomFrom={setCustomFrom}
              customTo={customTo}
              setCustomTo={setCustomTo}
            />
          </div>

          <div className="h-5 w-px bg-slate-200 hidden sm:block" />

          <div className="w-[165px]">
            <Select value={selectedStage} onValueChange={setSelectedStage}>
              <SelectTrigger className="h-8 text-xs bg-card font-medium">
                <SelectValue placeholder="Tất cả công đoạn" />
              </SelectTrigger>
              <SelectContent>
                {PARETO_STAGE_FILTERS.map((st) => (
                  <SelectItem key={st.code} value={st.code} className="text-xs">
                    {st.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="w-[175px]">
            <Select value={machineId} onValueChange={setMachineId}>
              <SelectTrigger className="h-8 text-xs bg-card">
                <SelectValue placeholder="Tất cả máy móc" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="text-xs">
                  -- Tất cả máy sản xuất --
                </SelectItem>
                {(machines as any)?.items?.map((m: any) => (
                  <SelectItem key={m.id} value={m.id} className="text-xs">
                    {m.name || m.model}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {(selectedStage !== "ALL" || selected4M !== "ALL") && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setSelectedStage("ALL");
                setSelected4M("ALL");
              }}
              className="h-7 px-2 text-[11px] text-rose-600 hover:text-rose-700 hover:bg-rose-50"
            >
              Xóa lọc ({selectedStage !== "ALL" ? selectedStage : ""}{" "}
              {selected4M !== "ALL" ? selected4M : ""})
            </Button>
          )}
        </div>

        <Button
          size="sm"
          onClick={handleExportExcel}
          disabled={!data && paretoItems.length === 0}
          className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5 h-8 text-xs font-semibold shadow-2xs"
        >
          <Download className="w-3.5 h-3.5" /> Xuất Báo Cáo QM (4 Sheets)
        </Button>
      </div>

      {/* ═══ TẦNG 1B: 5 THẺ KPI ĐIỀU HÀNH CHẤT LƯỢNG (QM EXECUTIVE SCORECARD) ═══ */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2.5">
        <KpiStatCard
          title="Tỷ lệ Đạt lần đầu (FPY)"
          value={stats?.passRate ?? 100}
          formatType="percent"
          colorVariant="emerald"
          icon={CheckCircle}
          badgeText={`${stats?.total || 0} lượt KCS`}
          trendLabel={`${stats?.passed || 0} lô đạt • ${stats?.inspecting || 0} đang kiểm`}
          direction="positive"
          isLoading={isLoading}
          testId="qc-kpi-pass"
        />

        <KpiStatCard
          title="Tỷ lệ Lỗi & Nút thắt"
          value={stats?.failRate ?? 0}
          formatType="percent"
          colorVariant="rose"
          icon={XCircle}
          badgeText={
            bottleneckStage
              ? `Nút thắt: ${bottleneckStage.shortLabel}`
              : "Ổn định"
          }
          trendLabel={`${stats?.failed || 0} lô lỗi • ${stats?.warning || 0} cảnh báo`}
          direction="inverse"
          isLoading={isLoading}
          testId="qc-kpi-fail"
        />

        <KpiStatCard
          title="Dự báo SPC kỳ tới"
          value={spcSummary?.forecastNextRatePct ?? 0}
          formatType="percent"
          colorVariant={
            spcSummary?.trendDirection === "WORSENING" ? "amber" : "blue"
          }
          icon={Gauge}
          badgeText={`CL: ${spcSummary?.centerLinePct ?? 0}% | UCL: ${spcSummary?.avgUclPct ?? 0}%`}
          trendLabel={
            spcSummary?.trendDirection === "IMPROVING"
              ? "Xu hướng giảm lỗi tốt"
              : spcSummary?.trendDirection === "WORSENING"
                ? `Cảnh báo tăng (${spcSummary?.outOfControlCount || 0} ngày vượt UCL)`
                : "Quá trình trong ngưỡng 3σ"
          }
          direction={
            spcSummary?.trendDirection === "WORSENING" ? "inverse" : "positive"
          }
          isLoading={isLoading}
          testId="qc-kpi-spc"
        />

        <KpiStatCard
          title="Tiến độ Xử lý NCR 8D"
          value={stats?.ncrResolutionPct ?? 100}
          formatType="percent"
          colorVariant="violet"
          icon={Activity}
          badgeText={`${stats?.ncrClosed || 0}/${stats?.ncrTotal || 0} Đã đóng`}
          trendLabel={
            (stats?.ncrOverdue || 0) > 0
              ? `${stats?.ncrOverdue} phiếu quá hạn CAPA`
              : `${stats?.ncrOpen || 0} phiếu đang xử lý`
          }
          direction="positive"
          isLoading={isLoading}
          testId="qc-kpi-ncr"
        />

        <KpiStatCard
          title="Chi phí Lỗi (COPQ)"
          value={stats?.copqTotal || 0}
          unit="đ"
          formatType="currency"
          colorVariant="amber"
          icon={DollarSign}
          badgeText={`${vitalFewCount} lỗi chiếm 80%`}
          trendLabel={`Hủy: ${(stats?.scrapCost || 0).toLocaleString("vi-VN")}đ`}
          direction="inverse"
          isLoading={isLoading}
          testId="qc-kpi-copq"
        />
      </div>

      {/* ═══ TẦNG 2: BỨC TRANH TIẾN ĐỘ & CHẤT LƯỢNG 8 CÔNG ĐOẠN (8-STAGE FPY PIPELINE) ═══ */}
      <Card
        className="shadow-2xs border-border bg-card overflow-hidden"
        data-testid="qc-8stage-pipeline"
      >
        <CardHeader className="py-2 px-3 border-b border-border bg-slate-50/70 flex flex-row items-center justify-between">
          <div className="flex items-center gap-2">
            <Layers className="w-3.5 h-3.5 text-blue-600" />
            <CardTitle className="text-[11px] font-bold uppercase tracking-wider text-slate-800">
              Ma Trận Chất Lượng & Tiến Độ 8 Công Đoạn (IQC → 6 Khâu Offset → FQC)
            </CardTitle>
          </div>
          <span className="text-[10px] text-slate-500">
            Nhấn vào từng công đoạn để lọc biểu đồ Pareto, 4M & SPC
          </span>
        </CardHeader>
        <CardContent className="p-2.5">
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
            {stageSummary.map((st: any) => {
              const isSelected = selectedStage === st.stageCode;
              const fpy = Number(st.fpyPct ?? 100);
              const statusColor =
                st.totalBatches === 0
                  ? "border-slate-200 bg-slate-50/40"
                  : st.isBottleneck
                    ? "border-rose-300 bg-rose-50/50"
                    : fpy >= 95
                      ? "border-emerald-200 bg-emerald-50/30"
                      : fpy >= 85
                        ? "border-amber-200 bg-amber-50/40"
                        : "border-rose-200 bg-rose-50/40";

              return (
                <button
                  key={st.stageCode}
                  type="button"
                  onClick={() =>
                    setSelectedStage(
                      isSelected ? "ALL" : st.stageCode,
                    )
                  }
                  className={`text-left p-2 rounded-lg border transition-all relative ${statusColor} ${
                    isSelected
                      ? "ring-2 ring-slate-900 border-slate-900 shadow-xs"
                      : "hover:border-slate-400"
                  }`}
                >
                  <div className="flex items-center justify-between gap-1 mb-1">
                    <span className="text-[10px] font-bold text-slate-800 truncate">
                      {st.shortLabel || st.stageCode}
                    </span>
                    {st.isBottleneck && (
                      <span className="px-1 py-0.2 rounded bg-rose-600 text-white text-[8px] font-bold uppercase shrink-0">
                        Nút thắt
                      </span>
                    )}
                  </div>

                  <div className="flex items-baseline justify-between">
                    <div>
                      <span
                        className={`text-sm font-extrabold font-mono ${
                          fpy >= 95
                            ? "text-emerald-700"
                            : fpy >= 85
                              ? "text-amber-700"
                              : "text-rose-700"
                        }`}
                      >
                        {fpy}%
                      </span>
                      <span className="text-[9px] text-slate-500 ml-1">FPY</span>
                    </div>
                    <span className="text-[10px] font-mono font-semibold text-rose-600">
                      {st.totalNg > 0 ? `${st.totalNg} NG` : "0 NG"}
                    </span>
                  </div>

                  <div className="w-full h-1 bg-slate-200 rounded-full overflow-hidden my-1">
                    <div
                      className={`h-full ${
                        fpy >= 95
                          ? "bg-emerald-500"
                          : fpy >= 85
                            ? "bg-amber-500"
                            : "bg-rose-500"
                      }`}
                      style={{ width: `${Math.max(8, Math.min(100, fpy))}%` }}
                    />
                  </div>

                  <div className="flex items-center justify-between text-[9px] text-slate-500">
                    <span>{st.totalBatches} lô</span>
                    {st.inspectingCount > 0 ? (
                      <span className="text-blue-600 font-semibold">
                        • {st.inspectingCount} đang kiểm
                      </span>
                    ) : (
                      <span>{st.failCount} lỗi</span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* ═══ TẦNG 3: BIỂU ĐỒ PARETO 80/20 CÓ ĐƯỜNG CONG PARETO TÍCH LŨY (0-100%) ═══ */}
      <Card
        className="shadow-2xs border-border bg-card overflow-hidden"
        data-testid="qc-pareto-card"
      >
        <CardHeader className="py-2 px-3.5 border-b border-border bg-muted/20 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <TrendingUp className="w-4 h-4 text-rose-600" />
            <CardTitle className="text-xs font-bold uppercase tracking-wide text-foreground">
              Biểu Đồ Pareto 80/20 & Đường Cong Tích Lũy Lỗi
            </CardTitle>
            {vitalFewCount > 0 && (
              <Badge className="bg-rose-100 text-rose-700 border border-rose-200 text-[10px] font-bold">
                {vitalFewCount} lỗi Vital Few chiếm ≥80% NG ({totalParetoNg} mẫu lỗi)
              </Badge>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-1">
            {PARETO_STAGE_FILTERS.map((st) => {
              const active = selectedStage === st.code;
              const stageCount = stageSummary.find(
                (s: any) => s.stage === st.code || s.stageCode === st.code,
              )?.totalQty;
              return (
                <button
                  key={st.code}
                  type="button"
                  onClick={() => setSelectedStage(st.code)}
                  className={`px-2 py-0.5 rounded text-[10px] font-semibold transition-colors border ${
                    active
                      ? "bg-slate-900 text-white border-slate-900"
                      : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                  }`}
                >
                  {st.label}
                  {stageCount ? ` (${stageCount})` : ""}
                </button>
              );
            })}
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-6 px-2 text-[10px] ml-1 gap-1"
              onClick={() => setShowParetoZoom(true)}
            >
              <Maximize2 className="w-3 h-3" /> Phóng lớn
            </Button>
          </div>
        </CardHeader>

        <CardContent className="p-3">
          {isParetoLoading && paretoItems.length === 0 ? (
            <div className="py-10 text-center text-xs text-muted-foreground">
              Đang tính toán đường cong Pareto 80/20...
            </div>
          ) : paretoItems.length === 0 ? (
            <div className="py-8 text-center text-xs text-muted-foreground">
              Không có lỗi NG phát sinh ở bộ lọc hiện tại.
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-3.5 items-start">
              {/* Biểu đồ Cột + Đường Cong Tích Lũy Pareto 2 Trục */}
              <div
                className="lg:col-span-7 h-[275px] w-full"
                data-testid="qc-pareto-chart-container"
              >
                <ResponsiveContainer
                  width="100%"
                  height={275}
                  minWidth={1}
                  minHeight={1}
                >
                  <ComposedChart
                    data={paretoChartData}
                    margin={{ top: 12, right: 18, left: -10, bottom: 20 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      stroke="#e2e8f0"
                    />
                    <XAxis
                      dataKey="defectCode"
                      fontSize={10}
                      stroke="#334155"
                      tickLine={false}
                      axisLine={{ stroke: "#cbd5e1" }}
                    />
                    <YAxis
                      yAxisId="left"
                      fontSize={10}
                      stroke="#334155"
                      tickLine={false}
                      axisLine={false}
                      label={{
                        value: "SL Lỗi (NG)",
                        angle: -90,
                        position: "insideLeft",
                        style: { fontSize: 9, fill: "#64748b" },
                      }}
                    />
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      domain={[0, 100]}
                      ticks={[0, 20, 40, 60, 80, 100]}
                      unit="%"
                      fontSize={10}
                      stroke="#d97706"
                      tickLine={false}
                      axisLine={false}
                    />
                    <RechartsTooltip
                      contentStyle={{
                        borderRadius: "8px",
                        border: "1px solid #cbd5e1",
                        fontSize: "11px",
                      }}
                      formatter={(val: any, name: any, props: any) => {
                        if (name === "Đường Pareto lũy kế (%)") {
                          return [
                            `${val}% (Tỷ trọng đơn: ${props?.payload?.percent ?? 0}%)`,
                            name,
                          ];
                        }
                        return [
                          `${val} mẫu (${props?.payload?.defectName || ""})`,
                          name,
                        ];
                      }}
                    />
                    <Legend
                      iconType="circle"
                      iconSize={8}
                      wrapperStyle={{ fontSize: 11, paddingTop: 4 }}
                    />
                    <ReferenceLine
                      yAxisId="right"
                      y={80}
                      stroke="#e11d48"
                      strokeWidth={1.5}
                      strokeDasharray="5 4"
                      label={{
                        value: "Ngưỡng 80% (Vital Few)",
                        position: "insideTopRight",
                        fill: "#e11d48",
                        fontSize: 10,
                        fontWeight: 700,
                      }}
                    />
                    <Bar
                      yAxisId="left"
                      dataKey="totalQty"
                      name="Số lượng lỗi (NG)"
                      fill="#e11d48"
                      radius={[4, 4, 0, 0]}
                      barSize={26}
                    >
                      {paretoChartData.map((entry: any, index: number) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={entry.isVitalFew ? "#e11d48" : "#3b82f6"}
                        />
                      ))}
                    </Bar>
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="cumulativePercent"
                      name="Đường Pareto lũy kế (%)"
                      stroke="#d97706"
                      strokeWidth={2.5}
                      dot={{
                        r: 4,
                        fill: "#d97706",
                        stroke: "#ffffff",
                        strokeWidth: 1.5,
                      }}
                      activeDot={{ r: 6, fill: "#b45309" }}
                      isAnimationActive={false}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>

              {/* Bảng Đối Chiếu Trọng Yếu 80/20 */}
              <div className="lg:col-span-5 border border-slate-200 rounded-lg overflow-hidden max-h-[275px] overflow-y-auto">
                <Table>
                  <TableHeader className="bg-slate-100 sticky top-0 z-10">
                    <TableRow className="text-[10px]">
                      <TableHead className="py-1.5 px-2">Mã / Tên lỗi</TableHead>
                      <TableHead className="py-1.5 px-1.5 text-center">
                        4M
                      </TableHead>
                      <TableHead className="py-1.5 px-2 text-right">
                        SL NG
                      </TableHead>
                      <TableHead className="py-1.5 px-2 text-right">
                        Tỷ trọng
                      </TableHead>
                      <TableHead className="py-1.5 px-2 text-right">
                        Lũy kế
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paretoItems.map((it: any) => (
                      <TableRow
                        key={`${it.stage}-${it.defectCode}`}
                        className={`text-xs ${
                          it.isVitalFew ? "bg-rose-50/50" : ""
                        }`}
                      >
                        <TableCell className="py-1 px-2">
                          <div className="flex items-center gap-1.5">
                            <span
                              className={`w-2 h-2 rounded-full shrink-0 ${
                                it.isVitalFew ? "bg-rose-600" : "bg-blue-500"
                              }`}
                            />
                            <span className="font-mono font-bold text-slate-900 text-[11px]">
                              {it.defectCode}
                            </span>
                          </div>
                          <div className="text-[10px] text-slate-600 truncate max-w-[165px]">
                            {it.defectName}
                          </div>
                        </TableCell>
                        <TableCell className="py-1 px-1.5 text-center">
                          <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 font-mono text-[9px] font-bold">
                            {it.rootCause4M || "MACHINE"}
                          </span>
                        </TableCell>
                        <TableCell className="py-1 px-2 text-right font-mono font-bold text-rose-600">
                          {it.totalQty}
                        </TableCell>
                        <TableCell className="py-1 px-2 text-right font-mono text-[11px] text-slate-600">
                          {it.percent}%
                        </TableCell>
                        <TableCell className="py-1 px-2 text-right font-mono font-bold">
                          <span
                            className={
                              it.isVitalFew
                                ? "text-amber-700 font-extrabold"
                                : "text-slate-500"
                            }
                          >
                            {it.cumulativePercent}%
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ═══ TẦNG 4: PHÂN TÍCH NGUYÊN NHÂN GỐC RỄ 4M/6M — BIỂU ĐỒ RADAR & SƠ ĐỒ XƯƠNG CÁ ISHIKAWA ═══ */}
      <div
        className="grid grid-cols-1 lg:grid-cols-12 gap-3"
        data-testid="qc-4m-ishikawa-section"
      >
        {/* Cột Trái (5 cols): Biểu Đồ Radar 4M/6M Đa Chiều */}
        <Card
          className="lg:col-span-5 shadow-2xs border-border bg-card flex flex-col"
          data-testid="qc-4m-radar-card"
        >
          <CardHeader className="py-2 px-3.5 border-b border-border bg-muted/20 flex flex-row items-center justify-between">
            <div className="flex items-center gap-1.5">
              <ShieldAlert className="w-4 h-4 text-blue-600" />
              <CardTitle className="text-xs font-bold uppercase tracking-wide text-foreground">
                Biểu Đồ Radar Phân Tích 4M/6M
              </CardTitle>
            </div>
            {selected4M !== "ALL" && (
              <Badge
                variant="outline"
                className="text-[10px] border-blue-300 bg-blue-50 text-blue-700 cursor-pointer"
                onClick={() => setSelected4M("ALL")}
              >
                Đang lọc: {selected4M} ✕
              </Badge>
            )}
          </CardHeader>
          <CardContent className="p-3 flex-1 flex flex-col justify-between">
            <div className="h-[235px] w-full">
              <ResponsiveContainer
                width="100%"
                height={235}
                minWidth={1}
                minHeight={1}
              >
                <RadarChart
                  cx="50%"
                  cy="50%"
                  outerRadius="72%"
                  data={analysis4M6M}
                >
                  <PolarGrid stroke="#cbd5e1" />
                  <PolarAngleAxis
                    dataKey="shortLabel"
                    tick={{ fill: "#1e293b", fontSize: 10, fontWeight: 700 }}
                  />
                  <PolarRadiusAxis
                    angle={30}
                    domain={[0, "auto"]}
                    tick={{ fill: "#64748b", fontSize: 9 }}
                    unit="%"
                  />
                  <Radar
                    name="% Lượng lỗi NG"
                    dataKey="defectSharePct"
                    stroke="#e11d48"
                    fill="#e11d48"
                    fillOpacity={0.35}
                    strokeWidth={2}
                    isAnimationActive={false}
                  />
                  <Radar
                    name="% Số vụ phát sinh"
                    dataKey="incidentSharePct"
                    stroke="#2563eb"
                    fill="#2563eb"
                    fillOpacity={0.2}
                    strokeWidth={2}
                    isAnimationActive={false}
                  />
                  <Legend
                    iconType="circle"
                    iconSize={8}
                    wrapperStyle={{ fontSize: 11 }}
                  />
                  <RechartsTooltip
                    contentStyle={{
                      borderRadius: "8px",
                      border: "1px solid #cbd5e1",
                      fontSize: "11px",
                    }}
                    formatter={(val: any, name: any) => [`${val}%`, name]}
                  />
                </RadarChart>
              </ResponsiveContainer>
            </div>

            {/* 6 nút chọn nhanh nhóm 4M/6M */}
            <div className="grid grid-cols-3 gap-1.5 pt-2 border-t border-slate-100">
              {analysis4M6M.map((m: any) => {
                const isActive = selected4M === m.code;
                return (
                  <button
                    key={m.code}
                    type="button"
                    onClick={() =>
                      setSelected4M(isActive ? "ALL" : m.code)
                    }
                    className={`p-1.5 rounded border text-left transition-all ${
                      isActive
                        ? "bg-slate-900 text-white border-slate-900"
                        : "bg-slate-50/70 hover:bg-slate-100 border-slate-200 text-slate-800"
                    }`}
                  >
                    <div className="flex items-center justify-between text-[10px] font-bold">
                      <span className="truncate">{m.shortLabel}</span>
                      <span
                        className={`font-mono ${
                          isActive ? "text-amber-300" : "text-rose-600"
                        }`}
                      >
                        {m.defectSharePct}%
                      </span>
                    </div>
                    <div
                      className={`text-[9px] font-mono ${
                        isActive ? "text-slate-300" : "text-slate-500"
                      }`}
                    >
                      {m.incidentCount} vụ • {m.defectQty} NG
                    </div>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Cột Phải (7 cols): Sơ Đồ Xương Cá Ishikawa Tương Tác (Interactive Fishbone Diagram) */}
        <Card
          className="lg:col-span-7 shadow-2xs border-border bg-card flex flex-col"
          data-testid="qc-ishikawa-fishbone-card"
        >
          <CardHeader className="py-2 px-3.5 border-b border-border bg-muted/20 flex flex-row items-center justify-between">
            <div className="flex items-center gap-1.5">
              <GitBranch className="w-4 h-4 text-rose-600" />
              <CardTitle className="text-xs font-bold uppercase tracking-wide text-foreground">
                Sơ Đồ Xương Cá Ishikawa (6M Root Cause & Defect Fishbone)
              </CardTitle>
            </div>
            <span className="text-[10px] text-slate-500">
              Click vào từng nhánh xương cá để lọc nguyên nhân
            </span>
          </CardHeader>

          <CardContent className="p-3 flex-1 flex flex-col justify-between">
            <div className="relative flex flex-col lg:flex-row items-stretch gap-2">
              {/* Thân cá: 3 Nhánh Trên + Trục Xương Sống Giữa + 3 Nhánh Dưới */}
              <div className="flex-1 flex flex-col justify-between space-y-1.5">
                {/* 3 Nhánh Xương Phía Trên: MAN - MACHINE - MATERIAL */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {topFishboneBones.map((bone: any) => {
                    const style =
                      FISHBONE_COLOR_MAP[bone.code] || FISHBONE_COLOR_MAP.MACHINE;
                    const isSelected = selected4M === bone.code;
                    return (
                      <div
                        key={bone.code}
                        onClick={() =>
                          setSelected4M(isSelected ? "ALL" : bone.code)
                        }
                        className={`relative rounded-lg border p-2 cursor-pointer transition-all ${style.border} ${style.bg} ${
                          isSelected
                            ? "ring-2 ring-slate-900 shadow-xs"
                            : "hover:brightness-95"
                        }`}
                      >
                        <div className="flex items-center justify-between gap-1 mb-1">
                          <span
                            className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${style.badge}`}
                          >
                            {bone.shortLabel}
                          </span>
                          <span className="font-mono text-[11px] font-extrabold text-slate-800">
                            {bone.defectSharePct}% ({bone.defectQty})
                          </span>
                        </div>
                        <div className="space-y-1 mt-1">
                          {(bone.causes || []).length === 0 ? (
                            <div className="text-[10px] text-slate-400 italic">
                              0 lỗi ghi nhận
                            </div>
                          ) : (
                            (bone.causes || []).slice(0, 3).map((c: any, idx: number) => (
                              <div
                                key={idx}
                                className="flex items-center justify-between gap-1 text-[10px] bg-white/80 px-1.5 py-0.5 rounded border border-slate-200/70"
                              >
                                <span className="truncate font-medium text-slate-700">
                                  ▸ {c.title}
                                </span>
                                <span className="font-mono font-bold text-rose-600 shrink-0">
                                  {c.qty}
                                </span>
                              </div>
                            ))
                          )}
                        </div>
                        {/* Đường xương chéo nối xuống xương sống */}
                        <div className="hidden sm:flex justify-center mt-1">
                          <div className="w-0.5 h-2.5 bg-slate-400 rotate-[-20deg]" />
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Xương Sống Trung Tâm (Central Fishbone Spine SVG) */}
                <div className="relative py-1.5 flex items-center">
                  <svg
                    className="w-full h-7 overflow-visible"
                    viewBox="0 0 600 28"
                    preserveAspectRatio="none"
                  >
                    {/* Đuôi cá bên trái */}
                    <polygon
                      points="0,2 18,14 0,26"
                      fill="#334155"
                      opacity="0.85"
                    />
                    {/* Trục xương sống chính */}
                    <line
                      x1="14"
                      y1="14"
                      x2="585"
                      y2="14"
                      stroke="#0f172a"
                      strokeWidth="3.5"
                    />
                    {/* Các đốt xương chéo trên và dưới */}
                    <line
                      x1="95"
                      y1="0"
                      x2="120"
                      y2="14"
                      stroke="#475569"
                      strokeWidth="2"
                    />
                    <line
                      x1="285"
                      y1="0"
                      x2="310"
                      y2="14"
                      stroke="#475569"
                      strokeWidth="2"
                    />
                    <line
                      x1="475"
                      y1="0"
                      x2="500"
                      y2="14"
                      stroke="#475569"
                      strokeWidth="2"
                    />
                    <line
                      x1="95"
                      y1="28"
                      x2="120"
                      y2="14"
                      stroke="#475569"
                      strokeWidth="2"
                    />
                    <line
                      x1="285"
                      y1="28"
                      x2="310"
                      y2="14"
                      stroke="#475569"
                      strokeWidth="2"
                    />
                    <line
                      x1="475"
                      y1="28"
                      x2="500"
                      y2="14"
                      stroke="#475569"
                      strokeWidth="2"
                    />
                    {/* Mũi tên dẫn vào Đầu cá */}
                    <polygon points="582,7 600,14 582,21" fill="#e11d48" />
                  </svg>
                </div>

                {/* 3 Nhánh Xương Phía Dưới: METHOD - MEASUREMENT - ENVIRONMENT */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {bottomFishboneBones.map((bone: any) => {
                    const style =
                      FISHBONE_COLOR_MAP[bone.code] || FISHBONE_COLOR_MAP.METHOD;
                    const isSelected = selected4M === bone.code;
                    return (
                      <div
                        key={bone.code}
                        onClick={() =>
                          setSelected4M(isSelected ? "ALL" : bone.code)
                        }
                        className={`relative rounded-lg border p-2 cursor-pointer transition-all ${style.border} ${style.bg} ${
                          isSelected
                            ? "ring-2 ring-slate-900 shadow-xs"
                            : "hover:brightness-95"
                        }`}
                      >
                        <div className="flex items-center justify-between gap-1 mb-1">
                          <span
                            className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${style.badge}`}
                          >
                            {bone.shortLabel}
                          </span>
                          <span className="font-mono text-[11px] font-extrabold text-slate-800">
                            {bone.defectSharePct}% ({bone.defectQty})
                          </span>
                        </div>
                        <div className="space-y-1 mt-1">
                          {(bone.causes || []).length === 0 ? (
                            <div className="text-[10px] text-slate-400 italic">
                              0 lỗi ghi nhận
                            </div>
                          ) : (
                            (bone.causes || []).slice(0, 3).map((c: any, idx: number) => (
                              <div
                                key={idx}
                                className="flex items-center justify-between gap-1 text-[10px] bg-white/80 px-1.5 py-0.5 rounded border border-slate-200/70"
                              >
                                <span className="truncate font-medium text-slate-700">
                                  ▸ {c.title}
                                </span>
                                <span className="font-mono font-bold text-rose-600 shrink-0">
                                  {c.qty}
                                </span>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Đầu Cá (Fish Head — Hậu Quả Chất Lượng Toàn Chuỗi) */}
              <div className="lg:w-[155px] shrink-0 flex flex-col justify-center">
                <div className="rounded-r-3xl rounded-l-lg border-2 border-rose-600 bg-gradient-to-br from-rose-600 to-rose-700 text-white p-3 shadow-sm flex flex-col justify-center items-center text-center h-full min-h-[120px]">
                  <span className="text-[9px] font-bold uppercase tracking-wider text-rose-100">
                    Đầu Cá (Hậu Quả)
                  </span>
                  <div className="text-xl font-black font-mono my-1">
                    {totalParetoNg} NG
                  </div>
                  <div className="text-[11px] font-semibold bg-white/15 px-2 py-0.5 rounded-full">
                    Tỷ lệ lỗi: {stats?.failRate ?? 0}%
                  </div>
                  {bottleneckStage && (
                    <div className="text-[9px] text-rose-100 mt-1.5 leading-tight">
                      Khâu trọng điểm:{" "}
                      <strong className="text-white">
                        {bottleneckStage.shortLabel}
                      </strong>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ═══ TẦNG 5: KIỂM SOÁT THỐNG KÊ SPC (p-CHART & DỰ BÁO) + TIẾN ĐỘ XỬ LÝ NCR 8D / COPQ ═══ */}
      <div
        className="grid grid-cols-1 lg:grid-cols-12 gap-3"
        data-testid="qc-spc-ncr-section"
      >
        {/* Cột Trái (7 cols): Biểu Đồ Kiểm Soát Quá Trình SPC (CL, UCL 3-Sigma & Forecast) */}
        <Card
          className="lg:col-span-7 shadow-2xs border-border bg-card"
          data-testid="qc-spc-control-card"
        >
          <CardHeader className="py-2 px-3.5 border-b border-border bg-muted/20 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div className="flex items-center gap-1.5">
              <Gauge className="w-4 h-4 text-indigo-600" />
              <CardTitle className="text-xs font-bold uppercase tracking-wide text-foreground">
                Biểu Đồ Kiểm Soát SPC (p-Chart 3σ) & Dự Báo Xu Hướng Lỗi
              </CardTitle>
            </div>
            {spcSummary && (
              <div className="flex items-center gap-1.5 text-[10px] font-mono">
                <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 font-bold">
                  CL: {spcSummary.centerLinePct}%
                </span>
                <span className="px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200 font-bold">
                  UCL (3σ): {spcSummary.avgUclPct}%
                </span>
                <span className="px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-200 font-bold flex items-center gap-0.5">
                  {spcSummary.trendDirection === "IMPROVING" ? (
                    <TrendingDown className="w-3 h-3 text-emerald-600" />
                  ) : (
                    <TrendingUp className="w-3 h-3 text-amber-600" />
                  )}
                  Dự báo: {spcSummary.forecastNextRatePct}%
                </span>
              </div>
            )}
          </CardHeader>
          <CardContent className="p-3">
            {spcChartData.length === 0 ? (
              <div className="py-10 text-center text-xs text-muted-foreground">
                Chưa đủ chuỗi ngày kiểm tra để dựng biểu đồ SPC trong kỳ này.
              </div>
            ) : (
              <div className="h-[245px] w-full">
                <ResponsiveContainer
                  width="100%"
                  height={245}
                  minWidth={1}
                  minHeight={1}
                >
                  <ComposedChart
                    data={spcChartData}
                    margin={{ top: 10, right: 15, left: -10, bottom: 10 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      stroke="#e2e8f0"
                    />
                    <XAxis
                      dataKey="label"
                      fontSize={10}
                      stroke="#475569"
                      tickLine={false}
                    />
                    <YAxis
                      yAxisId="left"
                      fontSize={10}
                      stroke="#475569"
                      unit="%"
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      fontSize={9}
                      stroke="#94a3b8"
                      tickLine={false}
                      axisLine={false}
                    />
                    <RechartsTooltip
                      contentStyle={{
                        borderRadius: "8px",
                        border: "1px solid #cbd5e1",
                        fontSize: "11px",
                      }}
                    />
                    <Legend
                      iconType="circle"
                      iconSize={8}
                      wrapperStyle={{ fontSize: 11 }}
                    />
                    <Bar
                      yAxisId="right"
                      dataKey="sampledQty"
                      name="Cỡ mẫu kiểm (n)"
                      fill="#e2e8f0"
                      radius={[3, 3, 0, 0]}
                      barSize={18}
                    />
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="centerLinePct"
                      name="Đường trung bình (CL %)"
                      stroke="#10b981"
                      strokeWidth={1.5}
                      strokeDasharray="4 4"
                      dot={false}
                      isAnimationActive={false}
                    />
                    <Line
                      yAxisId="left"
                      type="stepAfter"
                      dataKey="uclPct"
                      name="Giới hạn trên UCL (3σ %)"
                      stroke="#e11d48"
                      strokeWidth={1.5}
                      strokeDasharray="3 3"
                      dot={false}
                      isAnimationActive={false}
                    />
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="defectRatePct"
                      name="Tỷ lệ lỗi & Dự báo (%)"
                      stroke="#4f46e5"
                      strokeWidth={2.5}
                      dot={(props: any) => {
                        const { cx, cy, payload } = props;
                        if (payload?.isForecast) {
                          return (
                            <circle
                              key={`dot-${payload.dateKey}`}
                              cx={cx}
                              cy={cy}
                              r={5}
                              fill="#f59e0b"
                              stroke="#fff"
                              strokeWidth={2}
                            />
                          );
                        }
                        if (payload?.isOutOfControl) {
                          return (
                            <circle
                              key={`dot-${payload.dateKey}`}
                              cx={cx}
                              cy={cy}
                              r={5}
                              fill="#e11d48"
                              stroke="#fff"
                              strokeWidth={2}
                            />
                          );
                        }
                        return (
                          <circle
                            key={`dot-${payload?.dateKey}`}
                            cx={cx}
                            cy={cy}
                            r={3.5}
                            fill="#4f46e5"
                            stroke="#fff"
                            strokeWidth={1}
                          />
                        );
                      }}
                      isAnimationActive={false}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Cột Phải (5 cols): Tiến Độ Xử Lý NCR 8D & Cấu Trúc Chi Phí COPQ */}
        <Card
          className="lg:col-span-5 shadow-2xs border-border bg-card flex flex-col"
          data-testid="qc-ncr-pipeline-card"
        >
          <CardHeader className="py-2 px-3.5 border-b border-border bg-muted/20 flex flex-row items-center justify-between">
            <div className="flex items-center gap-1.5">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              <CardTitle className="text-xs font-bold uppercase tracking-wide text-foreground">
                Tiến Độ Xử Lý NCR 8D & Chi Phí COPQ
              </CardTitle>
            </div>
            <Badge
              variant="outline"
              className="text-[10px] font-bold bg-slate-100"
            >
              {ncrPipeline?.total || 0} Hồ sơ NCR
            </Badge>
          </CardHeader>
          <CardContent className="p-3 space-y-3 flex-1 flex flex-col justify-between">
            {/* Phễu 5 Bước Trạng Thái NCR 8D */}
            <div className="space-y-1.5">
              <div className="text-[10px] font-bold uppercase text-slate-500">
                Tiến độ đóng hồ sơ CAPA / 8D
              </div>
              {(ncrPipeline?.byStatus || []).map((st: any) => (
                <div key={st.status} className="space-y-0.5">
                  <div className="flex items-center justify-between text-[11px]">
                    <span className="font-medium text-slate-700">
                      {st.label}
                    </span>
                    <span className="font-mono font-bold text-slate-900">
                      {st.count} phiếu ({st.sharePct}%)
                    </span>
                  </div>
                  <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${
                        st.status === "CLOSED"
                          ? "bg-emerald-500"
                          : st.status === "VERIFICATION"
                            ? "bg-teal-500"
                            : st.status === "CORRECTIVE_ACTION"
                              ? "bg-blue-500"
                              : st.status === "INVESTIGATING"
                                ? "bg-amber-500"
                                : "bg-rose-500"
                      }`}
                      style={{
                        width: `${
                          st.count > 0 ? Math.max(8, st.sharePct) : 0
                        }%`,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Cơ cấu Chi Phí Chất Lượng Kém (COPQ Breakdown) & Quyết định xử lý */}
            <div className="pt-2 border-t border-slate-100 space-y-2">
              <div className="grid grid-cols-3 gap-1.5">
                <div className="p-2 rounded bg-rose-50/60 border border-rose-200/70">
                  <div className="text-[9px] text-rose-700 font-semibold uppercase">
                    Hủy hàng (Scrap)
                  </div>
                  <div className="text-xs font-mono font-bold text-rose-800 mt-0.5">
                    {(stats?.scrapCost || 0).toLocaleString("vi-VN")}đ
                  </div>
                </div>
                <div className="p-2 rounded bg-amber-50/60 border border-amber-200/70">
                  <div className="text-[9px] text-amber-700 font-semibold uppercase">
                    Gia công lại (Rework)
                  </div>
                  <div className="text-xs font-mono font-bold text-amber-800 mt-0.5">
                    {(stats?.reworkCost || 0).toLocaleString("vi-VN")}đ
                  </div>
                </div>
                <div className="p-2 rounded bg-blue-50/60 border border-blue-200/70">
                  <div className="text-[9px] text-blue-700 font-semibold uppercase">
                    In bù (Reprint)
                  </div>
                  <div className="text-xs font-mono font-bold text-blue-800 mt-0.5">
                    {(stats?.reprintCost || 0).toLocaleString("vi-VN")}đ
                  </div>
                </div>
              </div>

              {(ncrPipeline?.byDisposition || []).length > 0 && (
                <div className="flex flex-wrap items-center gap-1 pt-1">
                  {(ncrPipeline.byDisposition as any[]).map((dp) => (
                    <span
                      key={dp.code}
                      className="px-2 py-0.5 rounded bg-slate-100 text-slate-700 text-[10px] font-medium border border-slate-200"
                    >
                      {dp.label}: <strong>{dp.count}</strong>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ═══ TẦNG 6: NHẬT KÝ KIỂM TRA CHẤT LƯỢNG CHI TIẾT (IQC + IPQC + FQC) ═══ */}
      <Card className="shadow-2xs border-border bg-card overflow-hidden">
        <CardHeader className="py-2 px-3.5 border-b border-border bg-muted/20 flex flex-row items-center justify-between">
          <div className="flex items-center gap-2">
            <Filter className="w-3.5 h-3.5 text-slate-600" />
            <CardTitle className="text-xs font-bold uppercase tracking-wide text-foreground">
              Nhật Ký Kiểm Tra KCS Toàn Chuỗi ({combinedRecords.length} phiếu)
            </CardTitle>
          </div>
          {selectedStage !== "ALL" && (
            <Badge className="bg-slate-800 text-white text-[10px]">
              Khâu: {selectedStage}
            </Badge>
          )}
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="py-10 text-center text-xs text-muted-foreground">
              Đang tải dữ liệu QC...
            </div>
          ) : combinedRecords.length === 0 ? (
            <div className="py-10 text-center text-xs text-muted-foreground">
              Chưa có biên bản kiểm tra trong điều kiện lọc hiện tại.
            </div>
          ) : (
            <div className="overflow-x-auto max-h-[340px] overflow-y-auto">
              <Table>
                <TableHeader className="bg-muted/40 sticky top-0 z-10">
                  <TableRow>
                    <TableHead className="w-10 text-center text-xs">
                      STT
                    </TableHead>
                    <TableHead className="text-xs">Khâu KCS</TableHead>
                    <TableHead className="text-xs">Thời Gian</TableHead>
                    <TableHead className="text-xs">Lệnh SX / Lô</TableHead>
                    <TableHead className="text-xs">Sản Phẩm / Vật Tư</TableHead>
                    <TableHead className="text-xs text-right">Mẫu OK</TableHead>
                    <TableHead className="text-xs text-right">Mẫu NG</TableHead>
                    <TableHead className="text-xs text-center">
                      Kết Luận
                    </TableHead>
                    <TableHead className="text-xs">KCS Kiểm Tra</TableHead>
                    <TableHead className="text-xs">Ghi Chú</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {combinedRecords.slice(0, 50).map((r: any, idx: number) => {
                    const isPassed = r.status === "PASSED";
                    const isFailed = r.status === "FAILED";
                    const isInspecting = r.status === "INSPECTING";

                    return (
                      <TableRow
                        key={r.id || idx}
                        className={`text-xs hover:bg-muted/50 ${r.id ? "cursor-pointer" : ""}`}
                        onClick={() => {
                          // Bấm dòng mở chi tiết đúng biên bản (IQC / IPQC / FQC)
                          if (!r.id) return;
                          const to =
                            r.stageCode === "IQC"
                              ? "/qc-materials/$checkId"
                              : r.stageCode === "FQC"
                                ? "/qc-final/$checkId"
                                : "/qc-process/$checkId";
                          navigate({ to: to as any, params: { checkId: r.id } as any });
                        }}
                      >
                        <TableCell className="text-center font-mono text-muted-foreground py-1.5">
                          {idx + 1}
                        </TableCell>
                        <TableCell className="py-1.5">
                          <Badge variant="outline" className="text-[10px]">
                            {r.stepLabel}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground font-mono py-1.5">
                          {r.checkTime
                            ? format(new Date(r.checkTime), "dd/MM/yyyy HH:mm")
                            : "-"}
                        </TableCell>
                        <TableCell className="font-mono font-bold text-foreground py-1.5">
                          {r.poLabel}
                        </TableCell>
                        <TableCell className="font-medium text-foreground py-1.5 max-w-[200px] truncate">
                          {r.codeLabel}
                        </TableCell>
                        <TableCell className="text-right font-mono text-emerald-700 font-semibold py-1.5">
                          {r.okCount}
                        </TableCell>
                        <TableCell className="text-right font-mono text-rose-600 font-bold py-1.5">
                          {r.ngCount}
                        </TableCell>
                        <TableCell className="text-center py-1.5">
                          <span
                            className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold ${
                              isPassed
                                ? "bg-emerald-500/10 text-emerald-700 border border-emerald-500/20"
                                : isFailed
                                  ? "bg-rose-500/10 text-rose-700 border border-rose-500/20"
                                  : isInspecting
                                    ? "bg-blue-500/10 text-blue-700 border border-blue-500/20"
                                    : "bg-amber-500/10 text-amber-700 border border-amber-500/20"
                            }`}
                          >
                            {isPassed
                              ? "ĐẠT"
                              : isFailed
                                ? "LỖI"
                                : isInspecting
                                  ? "ĐANG KIỂM"
                                  : "CẢNH BÁO"}
                          </span>
                        </TableCell>
                        <TableCell className="text-muted-foreground py-1.5">
                          {r.inspector?.fullName || "-"}
                        </TableCell>
                        <TableCell className="text-muted-foreground truncate max-w-[180px] py-1.5">
                          {r.notes || "—"}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ═══ ZOOM MODAL PHÂN TÍCH PARETO 80/20 (Rule 4) ═══ */}
      <Dialog open={showParetoZoom} onOpenChange={setShowParetoZoom}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-rose-600" />
              Đối Chiếu Chi Tiết Biểu Đồ Đường Pareto 80/20 Theo Công Đoạn & 4M
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="h-[340px] w-full border border-slate-200 rounded-lg p-3 bg-slate-50/40">
              <ResponsiveContainer
                width="100%"
                height="100%"
                minWidth={1}
                minHeight={1}
              >
                <ComposedChart
                  data={paretoItems}
                  margin={{ top: 10, right: 20, left: 0, bottom: 20 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke="#e2e8f0"
                  />
                  <XAxis dataKey="defectCode" fontSize={11} stroke="#334155" />
                  <YAxis yAxisId="left" fontSize={11} stroke="#334155" />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    domain={[0, 100]}
                    unit="%"
                    fontSize={11}
                    stroke="#d97706"
                  />
                  <RechartsTooltip />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <ReferenceLine
                    yAxisId="right"
                    y={80}
                    stroke="#e11d48"
                    strokeDasharray="4 4"
                    label={{
                      value: "Ngưỡng 80%",
                      position: "right",
                      fill: "#e11d48",
                      fontSize: 11,
                    }}
                  />
                  <Bar
                    yAxisId="left"
                    dataKey="totalQty"
                    name="Tổng SL lỗi (NG)"
                    radius={[4, 4, 0, 0]}
                    barSize={32}
                  >
                    {paretoItems.map((entry: any, index: number) => (
                      <Cell
                        key={`zoom-cell-${index}`}
                        fill={entry.isVitalFew ? "#e11d48" : "#2563eb"}
                      />
                    ))}
                  </Bar>
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="cumulativePercent"
                    name="Đường Pareto lũy kế (%)"
                    stroke="#d97706"
                    strokeWidth={3}
                    dot={{ r: 4.5, fill: "#d97706", stroke: "#fff", strokeWidth: 1.5 }}
                    isAnimationActive={false}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <Table>
                <TableHeader className="bg-slate-100">
                  <TableRow className="text-xs">
                    <TableHead className="w-10 text-center">#</TableHead>
                    <TableHead>Mã lỗi</TableHead>
                    <TableHead>Tên lỗi</TableHead>
                    <TableHead>Công đoạn</TableHead>
                    <TableHead className="text-center">Nhóm 4M</TableHead>
                    <TableHead className="text-center">Mức độ</TableHead>
                    <TableHead className="text-right">SL lỗi (NG)</TableHead>
                    <TableHead className="text-right">Số lô lặp</TableHead>
                    <TableHead className="text-right">Tỷ trọng</TableHead>
                    <TableHead className="text-right">Lũy kế</TableHead>
                    <TableHead className="text-center">Nhóm 80/20</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paretoItems.map((it: any, i: number) => (
                    <TableRow key={i} className="text-xs">
                      <TableCell className="text-center font-mono">
                        {i + 1}
                      </TableCell>
                      <TableCell className="font-mono font-bold">
                        {it.defectCode}
                      </TableCell>
                      <TableCell className="font-medium">
                        {it.defectName}
                      </TableCell>
                      <TableCell className="font-mono text-[11px]">
                        {it.stageShortLabel || it.stage}
                      </TableCell>
                      <TableCell className="text-center font-mono text-[10px] font-bold">
                        {it.rootCause4M || "MACHINE"}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge
                          variant="outline"
                          className={
                            it.severity === "CRITICAL"
                              ? "border-rose-300 bg-rose-50 text-rose-700 text-[10px]"
                              : it.severity === "MAJOR"
                                ? "border-amber-300 bg-amber-50 text-amber-700 text-[10px]"
                                : "border-slate-300 bg-slate-50 text-slate-600 text-[10px]"
                          }
                        >
                          {it.severity}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono font-bold text-rose-600">
                        {it.totalQty}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {it.occurrences}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {it.percent}%
                      </TableCell>
                      <TableCell className="text-right font-mono font-bold text-amber-700">
                        {it.cumulativePercent}%
                      </TableCell>
                      <TableCell className="text-center">
                        {it.isVitalFew ? (
                          <Badge className="bg-rose-600 text-white text-[10px]">
                            80% Trọng yếu
                          </Badge>
                        ) : (
                          <span className="text-[11px] text-slate-400">
                            20% Thứ yếu
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
